import {
  Box,
  Menu,
  Paper,
  MenuItem,
  MenuList,
  Typography,
  ListItemIcon,
  ListItemText,
  hexToRgb,
  IconButton,
  FormGroup,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';
import Tooltip from '@mui/material/Tooltip';
import PhotoSizeSelectLargeIcon from '@mui/icons-material/PhotoSizeSelectLarge';
import { useDispatch, useSelector } from 'react-redux';
import LockIcon from '@mui/icons-material/Lock';
import { ExpertContent } from 'pages/media-library/interface';
import * as React from 'react';
import { selectSelectedFiles } from 'pages/media-library/selector';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  BoldAudioIcon,
  BoldExpandMoreIcon, BoldFolderIcon, BoldPlayIcon, BoldShareIcon, TrashIcon,
} from 'assets/icons';
import PublicIcon from '@mui/icons-material/Public';
import { getFileSize } from 'utils/formats';
import pdfDocument from 'assets/images/pdf-document.png';
import { FileType, urlExists } from 'utils/file-uploader';
import ShareModal from 'components/organisms/share-modal';
import { getUserHubUrl } from 'utils/common-utils';
import { GenerateVideoThumbnail, useConfirmation } from 'base/common';
import { ContentCut, FileDownload } from '@mui/icons-material';
import { selectEnterprise } from 'base/core/selectors';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import { mediaLibraryAction } from '../../slice';

export interface MediaItemProps {
  media: ExpertContent;
  direction?: 'column' | 'row';
  downloadSingleFile: (file: ExpertContent) => void;
  copyLink: (link: ExpertContent) => void;
  openTrimModel: (media: ExpertContent) => void;
}

const MediaItem: React.FC<MediaItemProps> = ({
  media, direction, downloadSingleFile, copyLink, openTrimModel,
}) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const dispatch = useDispatch();
  const { entLogo } = useSelector(selectEnterprise);
  const selectedFiles = useSelector(selectSelectedFiles);
  const location = useLocation();
  const navigate = useNavigate();
  const userHubUrl = getUserHubUrl();
  const confirmAlert = useConfirmation();
  const { _id: mediaId, fileCategory, thumbnail } = media;

  const isSelected = React.useMemo(
    () => selectedFiles.includes(mediaId),
    [selectedFiles, mediaId],
  );

  const [openShareMedia, setOpenShareMedia] = React.useState(false);
  const [openThumbnailModal, setOpenThumbnailModal] = React.useState({
    open: false,
    mediaId: '',
    mediaUrl: '',
    mediaExt: '',
  });
  const [click, setClick] = React.useState(false);

  const handleShareMediaOpen = () => setOpenShareMedia(true);
  const handleShareMediaClose = () => {
    setOpenShareMedia(false);
    dispatch(mediaLibraryAction.updateMedia({
      id: mediaId,
      isPublic: true,
    }));
  };

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const onDeleteMediaHanlder = () => {
    confirmAlert({
      variant: 'confirm',
      icon: 'warning',
      message: 'Are you sure you want to delete this media?',
      confirmButtonTxt: 'Yes',
      cancelButtonTxt: 'Cancel',
    })
      .then(() => {
        dispatch(
          mediaLibraryAction.deleteMedia({
            id: mediaId,
            contentUrl: media?.contentUrl ? media.contentUrl : null,
            fileSize: +media.fileSize,
          }),
        );
      });
  };

  const onShareMediaHandler = async () => {
    confirmAlert({
      variant: 'confirm',
      icon: 'warning',
      message: 'Sharing this media will make it public. Are you sure?',
      confirmButtonTxt: 'Yes',
      cancelButtonTxt: 'Cancel',
    })
      .then(() => {
        handleShareMediaOpen();
      });
  };

  const onChangePublicMediaHandler = () => {
    dispatch(mediaLibraryAction.updateMedia({
      id: mediaId,
      isPublic: false,
    }));
  };

  const onMediaClickHandler = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();

    if (fileCategory === FileType.FOLDER) {
      navigate(`${location.pathname}/${media.title}`);
      return;
    }

    if (typeof window !== undefined) {
      window.open(media.contentUrl, '_blank');
    }
  };

  const mediaThumbnail: string = React.useMemo(() => {
    if (fileCategory === 'image') {
      return media.contentUrl;
    }

    if (fileCategory === 'video' && thumbnail !== '') {
      if (urlExists(thumbnail)) {
        return thumbnail;
      }
    }

    if (fileCategory === 'document') {
      return pdfDocument;
    }

    return entLogo;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mediaId, entLogo]);

  return (
    <>
      <ShareModal
        open={openShareMedia}
        onClose={handleShareMediaClose}
        modalTitle="Share this session"
        shareTitle={media.title}
        shareUrl={`${userHubUrl}/experts/media/shared/${mediaId}`}
      />
      {openThumbnailModal.open && openThumbnailModal.mediaUrl && openThumbnailModal.mediaUrl !== '' && (
        <GenerateVideoThumbnail
          open={openThumbnailModal.open}
          onClose={() => setOpenThumbnailModal({ ...openThumbnailModal, open: false })}
          videoUrl={openThumbnailModal.mediaUrl}
          videoExt={openThumbnailModal.mediaExt}
          onFileCreate={({ fileName, url }) => {
            dispatch(mediaLibraryAction.updateMedia({
              id: openThumbnailModal.mediaId,
              thumbnailGenerated: fileName,
            }));
          }}
        />
      )}
      <Box
        sx={{
          display: 'flex',
          '> div': { flex: direction === 'column' ? 'auto' : 1 },
          flexDirection: direction,
          boxShadow: (t) => t.custom.boxShadow,
          background: '#fff',
          border: (t) => (isSelected
            ? `2px solid ${t.palette.primary.dark}`
            : `2px solid ${t.palette.background.default}`),
          borderRadius: (t) => `calc(${t.custom.borderRadius} + 2px)`,
          '&:hover': {
            '.media-item-name': {
              color: (t) => t.palette.primary.main,
            },
            '.media-item-overlay': {
              background: (t) => {
                let color = hexToRgb(t.palette.primary.main);
                color = color.replace('rgb', 'rgba');
                color = color.replace(')', ', 0.1)');
                return color;
              },
            },
          },
        }}
      >
        <Box
          display="flex"
          sx={{
            position: 'relative',
            borderTopLeftRadius: (t) => t.custom.borderRadius,
            borderTopRightRadius: (t) => (direction === 'column' ? t.custom.borderRadius : 0),
            borderBottomLeftRadius: (t) => (direction === 'row' ? t.custom.borderRadius : 0),
          }}
          height={125}
          alignItems="center"
          flexDirection="column"
          justifyContent="center"
        >
          {fileCategory === 'image' && (
            <Box
              component="img"
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                borderTopLeftRadius: (t) => t.custom.borderRadius,
                borderTopRightRadius: (t) => (direction === 'column' ? t.custom.borderRadius : 0),
                borderBottomLeftRadius: (t) => (direction === 'row' ? t.custom.borderRadius : 0),
              }}
              src={mediaThumbnail}
            />
          )}

          {fileCategory === 'video' && thumbnail !== '' && (
            <Box
              component="img"
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                borderTopLeftRadius: (t) => t.custom.borderRadius,
                borderTopRightRadius: (t) => (direction === 'column' ? t.custom.borderRadius : 0),
                borderBottomLeftRadius: (t) => (direction === 'row' ? t.custom.borderRadius : 0),
              }}
              src={mediaThumbnail}
            />
          )}

          {fileCategory === 'document' && (
            <Box
              component="img"
              sx={{
                width: '50%',
                height: 'auto',
                objectFit: 'cover',
                borderTopLeftRadius: (t) => t.custom.borderRadius,
                borderTopRightRadius: (t) => (direction === 'column' ? t.custom.borderRadius : 0),
                borderBottomLeftRadius: (t) => (direction === 'row' ? t.custom.borderRadius : 0),
              }}
              src={mediaThumbnail}
            />
          )}

          <Box
            className="media-item-overlay"
            sx={{
              position: 'absolute',
              inset: 0,
              transition: 'background 0.3s ease-in-out',
              borderTopLeftRadius: (t) => t.custom.borderRadius,
              borderTopRightRadius: (t) => t.custom.borderRadius,
              background: (t) => {
                let color = hexToRgb(t.palette.primary.main);
                color = color.replace('rgb', 'rgba');

                if (
                  fileCategory === FileType.FOLDER
                  || fileCategory === FileType.AUDIO
                  || fileCategory === FileType.DOCUMENT
                ) {
                  color = color.replace(')', ', 0.1)');
                }

                if (
                  fileCategory === FileType.IMAGE
                  || fileCategory === FileType.VIDEO
                ) {
                  color = color.replace(')', ', 0.3)');
                }

                return color;
              },
            }}
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Box sx={{ position: 'absolute', top: 1, right: 1 }}>
              <IconButton onClick={handleClick}>
                <BoldExpandMoreIcon
                  sx={{
                    fontSize: 14,
                    color: (t) => {
                      if (
                        fileCategory === 'folder'
                        || fileCategory === 'document'
                      ) {
                        return t.palette.primary.main;
                      }

                      return t.palette.common.white;
                    },
                  }}
                />
              </IconButton>
            </Box>

            <Box sx={{ position: 'absolute', top: 1, left: 9 }}>
              <FormGroup>
                <FormControlLabel
                  control={<Checkbox size="small" />}
                  label=""
                  checked={isSelected}
                  onClick={(e) => e.stopPropagation()}
                  onChange={() => dispatch(mediaLibraryAction.toggleSelectedFile(mediaId))}
                  sx={{
                    '.MuiTypography-body1': {
                      color: (t) => t.palette.grey.A100,
                      fontWeight: 'medium',
                    },
                  }}
                />
              </FormGroup>
            </Box>

            {fileCategory === 'video' && (
              <BoldPlayIcon
                sx={{ color: (t) => t.palette.common.white, fontSize: 30 }}
              />
            )}

            {fileCategory === 'folder' && (
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                width={50}
                height={50}
                sx={{
                  background: (t) => t.palette.grey.A100,
                  borderRadius: '50%',
                }}
              >
                <BoldFolderIcon
                  sx={{ color: (t) => t.palette.primary.main }}
                />
              </Box>
            )}

            {fileCategory === 'audio' && (
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                width={50}
                height={50}
                sx={{
                  background: (t) => t.palette.grey.A100,
                  borderRadius: '50%',
                }}
              >
                <BoldAudioIcon
                  sx={{ color: (t) => t.palette.primary.main }}
                />
              </Box>
            )}
          </Box>
        </Box>

        <Paper sx={{ p: 2, borderTopLeftRadius: 0, borderTopRightRadius: 0 }}>
          <Tooltip
            title={media.title}
            disableHoverListener={!media?.title || media.title.length < 20}
          >
            <Typography
              variant="body2"
              className="media-item-name"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                cursor: 'pointer',
              }}
              onClick={onMediaClickHandler}
            >
              {media.title}
            </Typography>
          </Tooltip>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
            Uploaded by You
          </Typography>
          <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
            {!media.isExternalFile && (
              <div className="col-8 col-md-8 col-lg-5 col-xl-9">
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {fileCategory !== FileType.FOLDER && `${getFileSize(media.fileSize)}`}
                  {fileCategory === FileType.FOLDER && 'Folder'}
                </Typography>
              </div>
            )}
            {media.isPublic && (
              <div className="col-4 col-md-4 col-lg-5 col-xl-3">
                <Box>
                  <PublicIcon color="primary" fontSize="small" sx={{ mt: '5px', mb: '-5px' }} />
                </Box>
              </div>
            )}
            {fileCategory !== 'folder' && (
              <div className="col-4 col-md-4 col-lg-5 col-xl-3">
                {!media.isExternalFile ? (
                  <IconButton disableRipple sx={{ p: 0 }} onClick={() => downloadSingleFile(media)}>
                    <FileDownload sx={{ color: (t) => t.palette.primary.main }} fontSize="small" />
                  </IconButton>
                ) : (
                  <IconButton
                    disableRipple
                    disableFocusRipple
                    sx={{ p: 0 }}
                    onClick={() => {
                      copyLink(media);
                      setClick(true);
                      setTimeout(() => {
                        setClick(false);
                      }, 2000);
                    }}
                  >
                    {click ? <Tooltip title="Copied!" arrow><CheckRoundedIcon color="primary" fontSize="small" /></Tooltip> : <Tooltip title="Copy Link to Clipboard" arrow><LinkIcon color="primary" fontSize="small" /></Tooltip>}
                  </IconButton>
                )}
              </div>
            )}

          </div>
        </Paper>
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        sx={{ ul: { width: 187 } }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuList>
          <MenuItem onClick={onDeleteMediaHanlder}>
            <ListItemIcon>
              <TrashIcon
                sx={{ fontSize: 20, color: (t) => t.palette.primary.main }}
              />
            </ListItemIcon>
            <ListItemText>
              <Typography fontSize={{ xs: 14 }}>Delete</Typography>
            </ListItemText>
          </MenuItem>
          {fileCategory !== 'folder' && (
            <MenuItem onClick={onShareMediaHandler}>
              <ListItemIcon>
                <BoldShareIcon
                  sx={{ fontSize: 20, color: (t) => t.palette.primary.main }}
                />
              </ListItemIcon>
              <ListItemText>
                <Typography fontSize={{ xs: 14 }}>Share</Typography>
              </ListItemText>
            </MenuItem>
          )}
          {media.isPublic && fileCategory !== 'folder' && (
            <MenuItem onClick={onChangePublicMediaHandler}>
              <ListItemIcon>
                <LockIcon
                  sx={{ fontSize: 20, color: (t) => t.palette.primary.main }}
                />
              </ListItemIcon>
              <ListItemText>
                <Typography fontSize={{ xs: 14 }}>Make Private</Typography>
              </ListItemText>
            </MenuItem>
          )}
          {fileCategory === 'video' && (
            <MenuItem onClick={() => setOpenThumbnailModal({
              open: true, mediaUrl: media.contentUrl, mediaId, mediaExt: media.fileType.replace('.', '').toLowerCase(),
            })}
            >
              <ListItemIcon>
                <PhotoSizeSelectLargeIcon
                  sx={{ fontSize: 20, color: (t) => t.palette.primary.main }}
                />
              </ListItemIcon>
              <ListItemText>
                <Typography fontSize={{ xs: 14 }}>Generate thumbnail</Typography>
              </ListItemText>
            </MenuItem>
          )}
          {!media.isExternalFile && fileCategory === 'video' && (
            <MenuItem onClick={() => openTrimModel(media)}>
              <ListItemIcon>
                <ContentCut
                  sx={{ fontSize: 20, color: (t) => t.palette.primary.main }}
                />
              </ListItemIcon>
              <ListItemText>
                <Typography fontSize={{ xs: 14 }}>Trim Video</Typography>
              </ListItemText>
            </MenuItem>
          )}
        </MenuList>
      </Menu>
    </>
  );
};

MediaItem.defaultProps = {
  direction: 'column',
};
export default MediaItem;
